/**
 * O que faz: carrega sob demanda as vendas do período e as datas de jornada do tempo até fechar.
 * Onde usa: BlocoTempoFechamentoVendas (/vendas e dashboard) e SecaoTempoFechamentoDashboard.
 * Conexões: React Query, tempoFechamentoService e cálculo em src/lib/vendas/tempoFechamento.ts.
 * Tabelas: vendas_realizadas, leads e metricas_conversas via serviço (somente SELECT com RLS).
 * Data: 05/10/2026.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buscarJornadaLeadsTempoFechamento, buscarVendasPeriodoTempoFechamento } from '@/services/tempoFechamentoService';

/**
 * Jornada dos leads que já estão na lista de Vendas (a aba já carregou as vendas do período).
 */
export function useJornadaLeadsTempoFechamento(clinicaId: string | null | undefined, leadIds: string[], inicioPeriodo: Date | null) {
  // Ordena os IDs para a chave do cache não mudar só porque a lista veio em outra ordem.
  const ids = useMemo(() => [...new Set(leadIds)].sort(), [leadIds]);
  return useQuery({
    queryKey: ['tempo-fechamento-jornada', clinicaId, inicioPeriodo?.toISOString() ?? null, ids],
    enabled: Boolean(clinicaId) && ids.length > 0,
    queryFn: ({ signal }) => {
      if (!clinicaId) throw new Error('Selecione uma clínica para calcular o tempo até fechar.');
      return buscarJornadaLeadsTempoFechamento(clinicaId, ids, inicioPeriodo, signal);
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}

/**
 * Vendas do CRM no período (só lead + data), para o dashboard, que não carrega a lista de Vendas.
 */
export function useVendasPeriodoTempoFechamento(clinicaId: string | null | undefined, inicio: Date | null, fim: Date | null) {
  return useQuery({
    queryKey: ['tempo-fechamento-vendas-periodo', clinicaId, inicio?.toISOString() ?? null, fim?.toISOString() ?? null],
    enabled: Boolean(clinicaId),
    queryFn: ({ signal }) => {
      if (!clinicaId) throw new Error('Selecione uma clínica para calcular o tempo até fechar.');
      return buscarVendasPeriodoTempoFechamento(clinicaId, inicio, fim, signal);
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}
